import React from 'react';
import { gridToWorld } from '../facilityCoords';
import { cornerPillarOffsets, FOUNDATION_HEIGHT } from '../buildingGeometry';
import { windowEmissiveScale } from '../dayNightCycle';
import { mullionLayout, plantBedLayout } from '../buildingDetails';

const FRAME = '#2a3330';
const GLASS = '#5fe39a';
const KNEE = '#1a201c';
const KNEE_ACCENT = '#3a5048';
const BED_COLORS = ['#2d5a3a', '#3a7a48'];
const MULLION_T = 0.035;
const DOUBLE_SIDE = 2; // THREE.DoubleSide

/**
 * Glass greenhouse with a peaked (gable) glass roof; interior stays visible.
 * @param {object} roomDef - FACILITY_ROOMS entry (type 'greenhouse')
 * @param {boolean} isSelected
 * @param {boolean} isHovered
 * @param {function} onClick
 * @param {function} onPointerEnter
 * @param {function} onPointerLeave
 */
export function GreenhouseBuilding({ roomDef, tick = 0, isSelected, isHovered, onClick, onPointerEnter, onPointerLeave }) {
  const { x, z, boxW, boxH, boxD } = gridToWorld(roomDef);
  const wallH = boxH;
  const kneeH = 0.35;
  const glassH = wallH - kneeH;
  const roofPeak = 0.9;
  const wallTop = FOUNDATION_HEIGHT + wallH;
  const glassCenterY = FOUNDATION_HEIGHT + kneeH + glassH / 2;

  const winGlow = windowEmissiveScale(tick);
  const emissiveIntensity = (isSelected ? 0.5 : isHovered ? 0.3 : 0.12) * (0.65 + winGlow * 0.35);
  const pillars = cornerPillarOffsets(boxW, boxD, 0.1);
  const ringRadius = Math.max(boxW, boxD) * 0.62;
  const mullions = mullionLayout(boxW, glassH, boxD);
  const beds = plantBedLayout(boxW * 0.85, boxD * 0.7, 3);

  const slopeLen = Math.hypot(boxW / 2, roofPeak);
  const angle = Math.atan2(roofPeak, boxW / 2);

  const interactive = !roomDef.noClick && !roomDef.ghost;

  const handlers = interactive
    ? {
        onClick: (e) => { e.stopPropagation(); onClick?.(); },
        onPointerEnter: (e) => { e.stopPropagation(); document.body.style.cursor = 'pointer'; onPointerEnter?.(); },
        onPointerLeave: () => { document.body.style.cursor = 'default'; onPointerLeave?.(); },
      }
    : {};

  return (
    <group position={[x, 0, z]} {...handlers}>
      {/* Foundation */}
      <mesh position={[0, FOUNDATION_HEIGHT / 2, 0]} receiveShadow castShadow>
        <boxGeometry args={[boxW + 0.12, FOUNDATION_HEIGHT, boxD + 0.12]} />
        <meshStandardMaterial color="#141915" roughness={1} metalness={0} />
      </mesh>

      {/* Knee wall */}
      <mesh position={[0, FOUNDATION_HEIGHT + kneeH / 2, 0]} castShadow receiveShadow>
        <boxGeometry args={[boxW, kneeH, boxD]} />
        <meshStandardMaterial color={KNEE} emissive={GLASS} emissiveIntensity={0.08} roughness={0.9} metalness={0.05} />
      </mesh>

      {/* Knee-wall top accent strip */}
      <mesh position={[0, FOUNDATION_HEIGHT + kneeH, 0]}>
        <boxGeometry args={[boxW, 0.04, boxD]} />
        <meshStandardMaterial color={KNEE_ACCENT} roughness={0.85} metalness={0.1} />
      </mesh>

      {/* Glass walls (single translucent shell) */}
      <mesh position={[0, glassCenterY, 0]}>
        <boxGeometry args={[boxW, glassH, boxD]} />
        <meshStandardMaterial
          color={GLASS}
          emissive={GLASS}
          emissiveIntensity={emissiveIntensity}
          transparent
          opacity={0.28}
          roughness={0.1}
          metalness={0.1}
          depthWrite={false}
        />
      </mesh>

      {/* Glass mullions */}
      {mullions.vertical.map((bar, i) => {
        if (bar.face === 'front') {
          return (
            <mesh key={`mv-f-${i}`} position={[bar.x, glassCenterY, boxD / 2]} castShadow>
              <boxGeometry args={[MULLION_T, glassH, MULLION_T]} />
              <meshStandardMaterial color={FRAME} roughness={0.6} metalness={0.4} />
            </mesh>
          );
        }
        if (bar.face === 'back') {
          return (
            <mesh key={`mv-b-${i}`} position={[bar.x, glassCenterY, -boxD / 2]} castShadow>
              <boxGeometry args={[MULLION_T, glassH, MULLION_T]} />
              <meshStandardMaterial color={FRAME} roughness={0.6} metalness={0.4} />
            </mesh>
          );
        }
        if (bar.face === 'left') {
          return (
            <mesh key={`mv-l-${i}`} position={[-boxW / 2, glassCenterY, bar.z]} castShadow>
              <boxGeometry args={[MULLION_T, glassH, MULLION_T]} />
              <meshStandardMaterial color={FRAME} roughness={0.6} metalness={0.4} />
            </mesh>
          );
        }
        return (
          <mesh key={`mv-r-${i}`} position={[boxW / 2, glassCenterY, bar.z]} castShadow>
            <boxGeometry args={[MULLION_T, glassH, MULLION_T]} />
            <meshStandardMaterial color={FRAME} roughness={0.6} metalness={0.4} />
          </mesh>
        );
      })}
      {mullions.horizontal.map((bar, i) => {
        const y = glassCenterY + bar.y;
        if (bar.face === 'front') {
          return (
            <mesh key={`mh-f-${i}`} position={[0, y, boxD / 2]} castShadow>
              <boxGeometry args={[boxW, MULLION_T, MULLION_T]} />
              <meshStandardMaterial color={FRAME} roughness={0.6} metalness={0.4} />
            </mesh>
          );
        }
        if (bar.face === 'back') {
          return (
            <mesh key={`mh-b-${i}`} position={[0, y, -boxD / 2]} castShadow>
              <boxGeometry args={[boxW, MULLION_T, MULLION_T]} />
              <meshStandardMaterial color={FRAME} roughness={0.6} metalness={0.4} />
            </mesh>
          );
        }
        if (bar.face === 'left') {
          return (
            <mesh key={`mh-l-${i}`} position={[-boxW / 2, y, 0]} castShadow>
              <boxGeometry args={[MULLION_T, MULLION_T, boxD]} />
              <meshStandardMaterial color={FRAME} roughness={0.6} metalness={0.4} />
            </mesh>
          );
        }
        return (
          <mesh key={`mh-r-${i}`} position={[boxW / 2, y, 0]} castShadow>
            <boxGeometry args={[MULLION_T, MULLION_T, boxD]} />
            <meshStandardMaterial color={FRAME} roughness={0.6} metalness={0.4} />
          </mesh>
        );
      })}

      {/* Interior plant beds */}
      {beds.map((bed, i) => (
        <mesh
          key={`bed-${i}`}
          position={[bed.x, FOUNDATION_HEIGHT + 0.15 + bed.h / 2, bed.z]}
          castShadow
        >
          <boxGeometry args={[bed.w, bed.h, bed.d]} />
          <meshStandardMaterial color={BED_COLORS[i % BED_COLORS.length]} roughness={0.95} metalness={0.05} />
        </mesh>
      ))}

      {/* Frame pillars */}
      {pillars.map(([dx, dz], i) => (
        <mesh key={i} position={[dx, FOUNDATION_HEIGHT + wallH / 2, dz]} castShadow>
          <boxGeometry args={[0.1, wallH, 0.1]} />
          <meshStandardMaterial color={FRAME} roughness={0.6} metalness={0.4} />
        </mesh>
      ))}

      {/* Peaked glass roof — two sloped panels meeting at a ridge along Z */}
      <mesh position={[-boxW / 4, wallTop + roofPeak / 2, 0]} rotation={[0, 0, angle]} castShadow>
        <boxGeometry args={[slopeLen, 0.04, boxD + 0.08]} />
        <meshStandardMaterial color={GLASS} emissive={GLASS} emissiveIntensity={emissiveIntensity} transparent opacity={0.32} roughness={0.1} metalness={0.1} depthWrite={false} side={DOUBLE_SIDE} />
      </mesh>
      <mesh position={[boxW / 4, wallTop + roofPeak / 2, 0]} rotation={[0, 0, -angle]} castShadow>
        <boxGeometry args={[slopeLen, 0.04, boxD + 0.08]} />
        <meshStandardMaterial color={GLASS} emissive={GLASS} emissiveIntensity={emissiveIntensity} transparent opacity={0.32} roughness={0.1} metalness={0.1} depthWrite={false} side={DOUBLE_SIDE} />
      </mesh>

      {/* Ridge beam */}
      <mesh position={[0, wallTop + roofPeak, 0]} castShadow>
        <boxGeometry args={[0.06, 0.06, boxD + 0.1]} />
        <meshStandardMaterial color={FRAME} roughness={0.5} metalness={0.5} />
      </mesh>

      {/* Ridge vents */}
      {[-boxD * 0.22, boxD * 0.22].map((vz, i) => (
        <mesh key={`vent-${i}`} position={[0, wallTop + roofPeak + 0.04, vz]} castShadow>
          <boxGeometry args={[0.14, 0.06, 0.28]} />
          <meshStandardMaterial color={FRAME} emissive={GLASS} emissiveIntensity={0.1} roughness={0.55} metalness={0.45} />
        </mesh>
      ))}

      {/* Selection ring */}
      {isSelected && (
        <mesh position={[0, 0.04, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <torusGeometry args={[ringRadius, 0.05, 8, 48]} />
          <meshStandardMaterial color={GLASS} emissive={GLASS} emissiveIntensity={1} roughness={0.3} />
        </mesh>
      )}
    </group>
  );
}
